<template>
    <div class="d2c-page">
        <container size="lg">
            <h1 class="d-none">
                Peekage - D2C Brands Solution | Digital Product Sampling Platform
            </h1>

            <section class="p-x-small-16">
                <Hero
                    image="new/peekage-product-sampling-program.png"
                    image-alt="Peekage: Product sampling program"
                    btn-text="Talk to an expert"
                    heading-text="Boost sales through targeted digital product sampling program"
                    image-width="w-100"
                />
            </section>

            <LogoGarden
                title="With Peekage's digital product sampling programs, get in front of qualified shoppers and conduct targeted in-home product trials. Build a perfect segment of natural shoppers with specific diets, interests, or behaviors with Peekage’s Audience Builder.
Peekage seamlessly matches your products with these qualified shoppers, helping you gather reviews, boost awareness, and drive sales quickly."
            />

            <section class="m-t-120 m-b-small-0 m-t-small-80" style="overflow: hidden">
                <Feature
                    v-for="item in features.slice(0, 4)"
                    :item="item"
                    :key="item.id"
                />

                <Testimonials :items="quotes" class="m-b-100" />

                <Feature
                    v-for="item in features.slice(4, 6)"
                    :item="item"
                    :key="item.id"
                />
            </section>

            <section data-aos="fade-in" data-aos-easing="linear">
                <HowItWorks :items="howItWorks" />
            </section>

            <section data-aos="fade-in" data-aos-easing="linear">
                <CustomerStories :stories="stories" />
            </section>
        </container>

        <container size="lg" class="p-t-96 p-t-small-60">
            <section class="p-l-small-0 p-r-small-0" data-aos="fade-in">
                <Banner
                    :btn-text="$device.mobile ? `Let's set a goal` : 'Book a demo'"
                >
                    <template #content>
                        <div class="p-r-68 p-r-small-0 p-r-medium-0 p-r-large-0 text-center text-lg-left">
                            <Heading
                                color="on-color"
                                class="f-w-600 f-s-small-28 l-h-small-36 text-center text-lg-left m-b-0"
                                tag="p"
                                size="x-lg"
                            >
                                Find out how peekage<br>can exceed your business goals
                            </Heading>
                        </div>
                    </template>
                </Banner>
            </section>
        </container>

        <div style="background-color: #F6F6F6;">
            <container size="lg">
                <div class="p-x-small-16">
                    <FaqMain :items="FAQItems" />
                </div>
            </container>
        </div>
    </div>
</template>

<script>
import FaqMain from '~/components/common/faq/FaqMain';
import AOS from 'aos';
import 'aos/dist/aos.css';
import smeData from '~/content/product/index';
import Hero from '~/components/common/hero';
import Feature from '~/components/common/feature';
import Banner from '~/components/common/banner';
import LogoGarden from '~/components/common/logo-garden/LogoGarden';
import Testimonials from '~/components/common/testimonials/Testimonials';
import CustomerStories from '~/components/common/custormer-stories/CustomerStories';
import HowItWorks from '~/components/common/how-it-works/HowItWorks';
import { breadcrumb } from '~/scripts/utils/constants/structured-data/index';

const _FAQ_ITEMS = [
    {
        title: 'What is a product sampling program, and how does it benefit my brand?',
        content: 'A product sampling program involves distributing free samples to targeted consumers, giving them a chance to experience a product firsthand. This strategy, offered by specialized product sampling companies, helps brands penetrate new segments, build awareness, and collect authentic feedback. By fostering consumer trust, it can significantly influence future purchase decisions.',
    },
    {
        title: 'Why should I choose Peekage as my product sampling agency?',
        content: 'Peekage is a leading product sampling platform that leverages a data-driven approach and advanced targeting to connect brands with the right audience. As a trusted product sampling company, Peekage offers an insightful digital solution that ensures your samples reach consumers most likely to engage, provide valuable feedback, and share their experiences—empowering your brand to grow effectively.',
    },
    {
        title: 'How does Peekage ensure that my product samples reach the right audience?',
        content: 'Peekage’s product sampling service utilizes demographic, geographic, psychographic, and interest-based data to connect your samples with consumers who align with your brand. This targeted approach, distinct among product sampling agencies, enhances engagement, boosts conversion rates, and drives valuable feedback to support your brand\'s growth.',
    },
    {
        title: 'How can I measure the success of my product sampling campaign, and what metrics are tracked?',
        content: 'Peekage offers comprehensive analytics to evaluate campaign effectiveness. Our product sampling solution provides detailed reports on engagement metrics, customer reviews, conversion rates, and sales indicators like purchase and repurchase rates. These insights help you measure the campaign’s impact on brand awareness and sales. Additionally, Peekage’s product testing services deliver valuable insights into customer sentiment and product acceptance, empowering you to make informed decisions for your brand.',
    },
    {
        title: 'Why is product sampling important for brand growth?',
        content: 'Product sampling programs give potential customers a chance to experience your product, often leading to word-of-mouth referrals and positive online reviews. This strategy, supported by top product sampling companies, can significantly increase brand loyalty, customer trust, and market reach, making it a valuable investment in brand-building.',
    },
    {
        title: 'How effective is digital product sampling compared to traditional methods?',
        content: 'Digital product sampling solutions, like those offered by Peekage, provide targeted reach and measurable results compared to traditional sampling. By using a digital platform, product testing agencies can ensure that samples are delivered to consumers who fit your brand’s profile, maximizing engagement and reducing waste.',
    },
    {
        title: 'What types of businesses benefit from product sampling programs?',
        content: 'Product sampling services are ideal for all consumer brands in industries such as beauty, health, and food and beverages. Whether you’re launching a new product or trying to expand market reach, partnering with a product sampling company or agency like Peekage helps introduce products to target audiences and gather impactful feedback for continuous improvement.',
    },
];

export default {
    components: {
        Hero,
        Feature,
        Banner,
        LogoGarden,
        Testimonials,
        CustomerStories,
        HowItWorks,
        FaqMain,
    },
    layout: 'xerneas',
    data() {
        return {
            FAQItems: [..._FAQ_ITEMS],
            features: smeData.features,
            quotes: smeData.quotes,
            stories: smeData.stories,
            howItWorks: smeData.howItWorks,
            additionalFeatures: [
                {
                    image: 'arts/Convert.svg',
                    title: 'Omnichannel sampling widget',
                    description: 'Seamlessly run targeted product sampling campaigns on your website or social media without coding.',
                },
                {
                    image: 'arts/Loyalty.svg',
                    title: 'Informed business decisions',
                    description: 'We’ll help you ask the right questions and collect consumer feedback that you can use to extract meaningful insights.',
                },
                {
                    image: 'arts/Fulfilment-lg.svg',
                    title: 'Fulfillment',
                    description: 'We’ll handle the delivery of your product samples, so that you can focus on building your brand. ',
                },
                {
                    image: 'arts/reviews-lg.svg',
                    title: 'Drive foot-traffic',
                    description: 'Peekage can help you drive foot-traffic through our in-store redemption options. ',
                },
            ],
            structuredDataWebPage: {
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                name: 'Product Sampling Program - Peekage',
                description: 'Peekage’s Product Sampling Program enables brands to connect with target consumers, distribute products effectively, gather real-time feedback, and analyze data for impactful campaigns.',
                url: 'https://peekage.com/product-sampling-program',
                isPartOf: {
                    '@type': 'WebSite',
                    name: 'Peekage',
                    url: 'https://peekage.com/',
                },
            },
            structuredDataOrganization: {
                '@context': 'https://schema.org',
                '@type': 'Organization',
                name: 'Peekage',
                url: 'https://peekage.com/',
                logo: 'https://peekage.com/logo.png',
                contactPoint: {
                    '@type': 'ContactPoint',
                    telephone: '+1-917-590-4410',
                    contactType: 'Business Inquiries',
                    availableLanguage: ['English'],
                },
                sameAs: [
                    'https://www.linkedin.com/company/peekage/',
                    'https://www.facebook.com/peekage',
                ],
            },
            structuredDataService: {
                '@context': 'https://schema.org',
                '@type': 'Service',
                name: 'Product Sampling Program',
                provider: {
                    '@type': 'Organization',
                    name: 'Peekage',
                    url: 'https://peekage.com/',
                },
                description: 'Peekage’s Product Sampling Program enables brands to connect with target consumers, distribute products effectively, gather real-time feedback, and analyze data for impactful campaigns.',
                areaServed: 'US & Canada',
                offers: {
                    '@type': 'Offer',
                    price: 'Contact for pricing',
                    priceCurrency: 'USD',
                    url: 'https://peekage.com/product-sampling-program',
                },
            },
            structuredDataFAQ: {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                    {
                        '@type': 'Question',
                        name: 'What is a product sampling program, and how does it benefit my brand?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'A product sampling program involves distributing free samples to targeted consumers, giving them a chance to experience a product firsthand. This strategy, offered by specialized product sampling companies, helps brands penetrate new segments, build awareness, and collect authentic feedback. By fostering consumer trust, it can significantly influence future purchase decisions.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'Why should I choose Peekage as my product sampling agency?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Peekage is a leading product sampling platform that leverages a data-driven approach and advanced targeting to connect brands with the right audience. As a trusted product sampling company, Peekage offers an insightful digital solution that ensures your samples reach consumers most likely to engage, provide valuable feedback, and share their experiences—empowering your brand to grow effectively.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'How does Peekage ensure that my product samples reach the right audience?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: "Peekage’s product sampling service utilizes demographic, geographic, psychographic, and interest-based data to connect your samples with consumers who align with your brand. This targeted approach, distinct among product sampling agencies, enhances engagement, boosts conversion rates, and drives valuable feedback to support your brand's growth.",
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'How can I measure the success of my product sampling campaign, and what metrics are tracked?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Peekage offers comprehensive analytics to evaluate campaign effectiveness. Our product sampling solution provides detailed reports on engagement metrics, customer reviews, conversion rates, and sales indicators like purchase and repurchase rates. These insights help you measure the campaign’s impact on brand awareness and sales. Additionally, Peekage’s product testing services deliver valuable insights into customer sentiment and product acceptance, empowering you to make informed decisions for your brand.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'Why is product sampling important for brand growth?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Product sampling programs give potential customers a chance to experience your product, often leading to word-of-mouth referrals and positive online reviews. This strategy, supported by top product sampling companies, can significantly increase brand loyalty, customer trust, and market reach, making it a valuable investment in brand-building.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'How effective is digital product sampling compared to traditional methods?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Digital product sampling solutions, like those offered by Peekage, provide targeted reach and measurable results compared to traditional sampling. By using a digital platform, product testing agencies can ensure that samples are delivered to consumers who fit your brand’s profile, maximizing engagement and reducing waste.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'What types of businesses benefit from product sampling programs?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Product sampling services are ideal for all consumer brands in industries such as beauty, health, and food and beverages. Whether you’re launching a new product or trying to expand market reach, partnering with a product sampling company or agency like Peekage helps introduce products to target audiences and gather impactful feedback for continuous improvement.',
                        },
                    },
                ],
            },

        };
    },
    head() {
        return {
            title: 'Digital Product Sampling Program - Sampling Company',
            __dangerouslyDisableSanitizers: ['script'],
            script: [
                {
                    innerHTML: JSON.stringify(this.structuredDataWebPage),
                    type: 'application/ld+json',
                },
                {
                    innerHTML: JSON.stringify(this.structuredDataOrganization),
                    type: 'application/ld+json',
                },
                {
                    innerHTML: JSON.stringify(this.structuredDataService),
                    type: 'application/ld+json',
                },
                { innerHTML: JSON.stringify(this.structuredDataFAQ), type: 'application/ld+json' },
                {
                    innerHTML: JSON.stringify(breadcrumb({
                        name: 'd2c brands Solutions',
                        uri: this.$route.path,
                    })),
                    type: 'application/ld+json',
                },
            ],
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Leverage Peekage’s versatile digital product sampling programs to promote your brand and products through highly targeted product sampling campaigns.',
                },
                {
                    hid: 'keywords',
                    name: 'keywords',
                    content: 'product sampling program',
                },
                {
                    property: 'og:title',
                    content: 'Peekage | Smart product sampling platform',
                    'data-hid': 'og:title',
                },
                {
                    property: 'og:url',
                    content: 'https://peekage.com',
                    'data-hid': 'og:url',
                },
                {
                    property: 'og:description',
                    content: 'Are you interested in a new product—but wanted to try it first? With Peekage, you can',
                    'data-hid': 'og:description',
                },
                {
                    property: 'og:image',
                    content: 'https://peekage.com/opengraph.png',
                    'data-hid': 'og:description',
                },
                {
                    property: 'twitter:title',
                    content: 'Peekage | Smart product sampling platform',
                    vmid: 'twitter:title',
                },
                {
                    property: 'twitter:url',
                    content: 'https://peekage.com',
                    vmid: 'twitter:url',
                },
                {
                    property: 'twitter:description',
                    content: 'Are you interested in a new product—but wanted to try it first? With Peekage, you can',
                    vmid: 'twitter:description',
                },
            ],
            link: [
                {
                    rel: 'canonical',
                    href: 'https://peekage.com/product-sampling-program',
                },
            ],
        };
    },
    mounted() {
        AOS.init();
    },
};
</script>
